import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { dataAPI } from "../api/dataApi";
import { createInboxItem } from "../api/inboxApi";
import type { ComponentWithSource } from "../api/componentsData";
import { normalizeOrgSegment } from "../utils/collectionPaths";
import { CollectionFieldPicker, NO_COLLECTION } from "./CollectionFieldPicker";
import { getAssignableFields, getCheckboxDefaults } from "./inboxFieldUtils";
import { useCreatableCollections } from "./useCreatableCollections";
import { useAuth } from "../auth/AuthContext";
import { rememberInboxField } from "./inboxAssignPrefs";

type QuickAddDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSavedToInbox?: () => void;
  onSavedToCollection?: () => void;
};

export function QuickAddDialog({
  open,
  onOpenChange,
  onSavedToInbox,
  onSavedToCollection,
}: QuickAddDialogProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [collectionSlug, setCollectionSlug] = useState(NO_COLLECTION);
  const [fieldName, setFieldName] = useState("");
  const [selectedComponent, setSelectedComponent] = useState<ComponentWithSource | null>(null);
  const [saving, setSaving] = useState(false);
  const { creatableCollections, loading: collectionsLoading } = useCreatableCollections(open);
  const { user } = useAuth();
  const persistenceUserId =
    user?.uid || user?.info?.uid || user?.id || user?._id || null;

  useEffect(() => {
    if (!open) {
      setText("");
      setCollectionSlug(NO_COLLECTION);
      setFieldName("");
      setSelectedComponent(null);
      return;
    }
    const timeoutId = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  const resetForAnother = useCallback(() => {
    setText("");
    window.setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error("Enter some text to save");
      return;
    }

    const directToCollection =
      collectionSlug && collectionSlug !== NO_COLLECTION && selectedComponent;

    if (directToCollection) {
      const assignable = getAssignableFields(selectedComponent);
      if (assignable.length === 0) {
        toast.error("This collection has no text field to assign to");
        return;
      }
      if (!fieldName) {
        toast.error("Select a field");
        return;
      }
    }

    setSaving(true);
    try {
      if (directToCollection && selectedComponent) {
        const payload: Record<string, unknown> = {
          [fieldName]: trimmed,
          ...getCheckboxDefaults(selectedComponent.fields),
        };
        const orgForApi = normalizeOrgSegment(selectedComponent.organization || "") || undefined;
        await dataAPI.createCollectionItem(selectedComponent.slug, payload, orgForApi);
        rememberInboxField(persistenceUserId, selectedComponent, fieldName);
        toast.success(`Added to ${selectedComponent.name || selectedComponent.slug}`);
        onSavedToCollection?.();
        resetForAnother();
        return;
      }

      await createInboxItem(trimmed);
      toast.success("Saved to inbox");
      onSavedToInbox?.();
      resetForAnother();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Could not save";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Quick Add</DialogTitle>
          <DialogDescription>
            Capture text to your inbox, or send it directly to a collection field.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="quick-add-text">Text</Label>
            <textarea
              id="quick-add-text"
              ref={textareaRef}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Type anything..."
              rows={5}
              className={cn(
                "flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              )}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                  event.preventDefault();
                  void handleSave();
                }
              }}
            />
          </div>

          <CollectionFieldPicker
            collections={creatableCollections}
            collectionSlug={collectionSlug}
            fieldName={fieldName}
            allowNone
            collectionsLoading={collectionsLoading}
            persistenceUserId={persistenceUserId}
            restoreFromPrefs={open}
            onCollectionChange={(slug, component) => {
              setCollectionSlug(slug || NO_COLLECTION);
              setSelectedComponent(component);
            }}
            onFieldChange={setFieldName}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Close
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving || !text.trim()}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
